import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { JiraProject } from "@p0security/shared/integrations/trackers/jira/types";
import type {
  TicketApprovalRule,
  TicketApprover,
} from "@p0security/shared/types/workflow/types";
import { Button, Flex, Input, Select } from "antd";
import React, { useCallback, useMemo, useState } from "react";

import { useGuardedEffect } from "../../../../hooks/useGuardedEffect.ts";
import { ErrorDisplay } from "../../../Error.tsx";
import { useAuthFetch, useHasRole } from "../../../Login/hook.ts";
import { StyledFormItem } from "../../../common/forms/styles.tsx";

const MappingEntry: React.FC<{
  ix: number;
  request: string;
  ticket: string;
  onRemove: (ix: number) => void;
  onUpdate: (ix: number, entry: [string, string]) => void;
}> = (props) => {
  const { ix, onRemove, onUpdate, request, ticket } = props;
  const handleRemove = useCallback(() => onRemove(ix), [ix, onRemove]);
  const handleEditRequest = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onUpdate(ix, [event.target.value, ticket]),
    [ix, onUpdate, ticket]
  );
  const handleEditTicket = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onUpdate(ix, [request, event.target.value]),
    [ix, onUpdate, request]
  );
  return (
    <Flex justify="flex-start" gap={8}>
      <Input value={request} onChange={handleEditRequest} />
      <Input value={ticket} onChange={handleEditTicket} />
      <Button danger icon={<DeleteOutlined />} onClick={handleRemove} />
    </Flex>
  );
};

export const TicketApproval: React.FC<{
  value: TicketApprovalRule;
  installed: TicketApprover[];
  onChange: (rule: TicketApprovalRule) => void;
}> = ({ value, onChange }) => {
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError);
  const canRefresh = useHasRole("owner");

  const [projects, setProjects] = useState<JiraProject[]>();

  useGuardedEffect(
    (cancellation) => async () => {
      if (!canRefresh) return;
      const response = await authFetch("integrations/jira/projects", {
        method: "GET",
      });
      if (!response) return;
      const { projects } = await response.json();
      cancellation.guard(setProjects)(projects);
    },
    [authFetch, canRefresh],
    setError
  );

  const projectItems = useMemo(
    () => (projects ?? []).map((p) => ({ value: p.id, label: p.name })),
    [projects]
  );

  const handleSelectProject = useCallback(
    (project: string) => onChange({ ...value, namespace: project }),
    [onChange, value]
  );

  const entries = useMemo(() => Object.entries(value.mapping), [value]);
  const onUpdateMappingEntries = useCallback(
    (entries: [string, string][]) =>
      onChange({ ...value, mapping: Object.fromEntries(entries) }),
    [onChange, value]
  );

  const handleAddMapEntry = useCallback(
    () => onUpdateMappingEntries([...entries, ["", ""]]),
    [onUpdateMappingEntries, entries]
  );
  const handleRemoveMapEntry = useCallback(
    (ix: number) =>
      onUpdateMappingEntries([
        ...entries.slice(0, ix),
        ...entries.slice(ix + 1, entries.length),
      ]),
    [onUpdateMappingEntries, entries]
  );
  const handleUpdateMapEntry = useCallback(
    (ix: number, entry: [string, string]) =>
      onUpdateMappingEntries([
        ...entries.slice(0, ix),
        entry,
        ...entries.slice(ix + 1, entries.length),
      ]),
    [onUpdateMappingEntries, entries]
  );

  return (
    <>
      {error && <ErrorDisplay error={error} />}
      <StyledFormItem label="Project">
        <Select
          options={projectItems}
          onChange={handleSelectProject}
          value={value.namespace}
        />
      </StyledFormItem>
      <StyledFormItem label="Mapping">
        <Flex justify="space-between">
          <Flex vertical gap={8}>
            {Object.entries(value.mapping).map(([request, ticket], ix) => (
              <MappingEntry
                key={ix}
                ix={ix}
                request={request}
                ticket={ticket}
                onUpdate={handleUpdateMapEntry}
                onRemove={handleRemoveMapEntry}
              />
            ))}
          </Flex>
          <Button icon={<PlusOutlined />} onClick={handleAddMapEntry}>
            Add
          </Button>
        </Flex>
      </StyledFormItem>
    </>
  );
};
